<template>
  <div class="main-content">
    <el-form :model="form" label-width="120px" class="homepage-form">
      <h2>{{ $t('homepageConfig.title') }}</h2>
      <el-alert type="info" :closable="false" style="margin-bottom: 16px;">
        {{ $t('homepageConfig.description') }}
      </el-alert>

      <!-- Header / Footer 共用 Logo -->
      <el-form-item :label="$t('homepageConfig.logoLabel')" prop="logo">
        <file-upload
          :tip="$t('homepageConfig.logoTip')"
          action="file/upload"
          :limit="1"
          :multiple="false"
          :fileUrls="form.logo || ''"
          @change="handleLogoChange"
        />
      </el-form-item>

      <el-form-item :label="$t('homepageConfig.backgroundColor')">
        <el-color-picker v-model="form.backgroundColor" show-alpha ></el-color-picker>
      </el-form-item>

      <!-- 主店基础信息 -->
      <el-form-item :label="$t('homepageConfig.mainAddress')">
        <el-input v-model="form.address" :placeholder="$t('homepageConfig.mainAddressPlaceholder')" />
      </el-form-item>
      <el-form-item :label="$t('homepageConfig.mainPhone')">
        <el-input v-model="form.phone" :placeholder="$t('homepageConfig.mainPhonePlaceholder')" />
      </el-form-item>

      <!-- 分店信息 -->
      <el-form-item :label="$t('homepageConfig.branchSection')">
        <el-button type="primary" size="mini" @click="addBranch">{{ $t('homepageConfig.addBranch') }}</el-button>
        <el-table :data="form.branches" size="small" style="margin-top: 8px;">
          <el-table-column :label="$t('homepageConfig.branchName')" width="180">
            <template slot-scope="scope">
              <el-input v-model="scope.row.name" :placeholder="$t('homepageConfig.branchNamePlaceholder')" />
            </template>
          </el-table-column>
          <el-table-column :label="$t('homepageConfig.branchAddress')">
            <template slot-scope="scope">
              <el-input v-model="scope.row.address" :placeholder="$t('homepageConfig.branchAddressPlaceholder')" />
            </template>
          </el-table-column>
          <el-table-column :label="$t('homepageConfig.branchPhone')" width="200">
            <template slot-scope="scope">
              <el-input v-model="scope.row.phone" :placeholder="$t('homepageConfig.branchPhonePlaceholder')" />
            </template>
          </el-table-column>
          <el-table-column :label="$t('table.actions')" width="80">
            <template slot-scope="scope">
              <el-button type="text" @click="removeBranch(scope.$index)">{{ $t('common.delete') }}</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-form-item>

      <!-- 社交账号 -->
      <el-form-item :label="$t('homepageConfig.socialSection')">
        <el-button type="primary" size="mini" @click="addSocial">{{ $t('homepageConfig.addSocial') }}</el-button>
        <el-table :data="form.socials" size="small" style="margin-top: 8px;">
          <el-table-column :label="$t('homepageConfig.socialPlatform')" width="180">
            <template slot-scope="scope">
              <el-input v-model="scope.row.platform" :placeholder="$t('homepageConfig.socialPlatformPlaceholder')" />
            </template>
          </el-table-column>
          <el-table-column :label="$t('homepageConfig.socialLink')">
            <template slot-scope="scope">
              <el-input v-model="scope.row.url" :placeholder="$t('homepageConfig.socialLinkPlaceholder')" />
            </template>
          </el-table-column>
          <el-table-column :label="$t('table.actions')" width="80">
            <template slot-scope="scope">
              <el-button type="text" @click="removeSocial(scope.$index)">{{ $t('common.delete') }}</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-form-item>

      <!-- 展示服务 -->
      <el-form-item :label="$t('homepageConfig.serviceSection')">
        <el-button type="primary" size="mini" @click="addService">{{ $t('homepageConfig.addServiceTag') }}</el-button>
        <el-table :data="form.services" size="small" style="margin-top: 8px;">
          <el-table-column :label="$t('homepageConfig.serviceName')">
            <template slot-scope="scope">
              <el-input v-model="scope.row.name" :placeholder="$t('homepageConfig.serviceNamePlaceholder')" />
            </template>
          </el-table-column>
          <el-table-column :label="$t('table.actions')" width="80">
            <template slot-scope="scope">
              <el-button type="text" @click="removeService(scope.$index)">{{ $t('common.delete') }}</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-form-item>

      <!-- 其他说明 -->
      <el-form-item :label="$t('homepageConfig.extraLabel')">
        <el-input
          type="textarea"
          :rows="3"
          v-model="form.extra"
          :placeholder="$t('homepageConfig.extraPlaceholder')"
        />
      </el-form-item>

      <el-form-item>
        <el-button type="primary" @click="saveConfig" :loading="saving">{{ $t('common.save') }}</el-button>
        <el-button @click="loadConfig" :disabled="loading">{{ $t('common.reset') }}</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import fileUpload from '@/components/common/FileUpload.vue'

export default {
  name: 'HomepageConfig',
  components: { fileUpload },
  data() {
    return {
      loading: false,
      saving: false,
      logoConfigId: null,
      footerConfigId: null,
      form: {
        logo: '',
        address: '',
        phone: '',
        branches: [],
        socials: [],
        services: [],
        extra: '',
        backgroundColor: ''
      }
    }
  },
  created() {
    this.loadConfig()
  },
  methods: {
    async loadConfig() {
      this.loading = true
      try {
        // 加载 logo 配置
        const logoRes = await this.$http({
          url: 'config/list',
          method: 'get',
          params: { page: 1, limit: 1, sort: 'id', name: 'homepage_logo' }
        })
        if (logoRes.data && logoRes.data.code === 0 && logoRes.data.data.list.length > 0) {
          const row = logoRes.data.data.list[0]
          this.logoConfigId = row.id
          this.form.logo = row.value || ''
        }

        // 加载 footer 配置
        const footerRes = await this.$http({
          url: 'config/list',
          method: 'get',
          params: { page: 1, limit: 1, sort: 'id', name: 'homepage_footer_config' }
        })
        if (footerRes.data && footerRes.data.code === 0 && footerRes.data.data.list.length > 0) {
          const row = footerRes.data.data.list[0]
          this.footerConfigId = row.id
          if (row.value) {
            try {
              const obj = JSON.parse(row.value)
              this.form = Object.assign(
                {},
                this.form,
                {
                  address: obj.address || '',
                  phone: obj.phone || '',
                  branches: obj.branches || [],
                  socials: obj.socials || [],
                  services: obj.services || [],
                  extra: obj.extra || '',
                  backgroundColor: obj.backgroundColor || ''
                }
              )
            } catch (e) {
              console.error('parse homepage_footer_config error', e)
            }
          }
        }
      } catch (e) {
        console.error('load homepage config error', e)
      } finally {
        this.loading = false
      }
    },
    handleLogoChange(fileUrls) {
      this.form.logo = fileUrls || ''
    },
    addBranch() {
      this.form.branches.push({ name: '', address: '', phone: '' })
    },
    removeBranch(index) {
      this.form.branches.splice(index, 1)
    },
    addSocial() {
      this.form.socials.push({ platform: '', url: '' })
    },
    removeSocial(index) {
      this.form.socials.splice(index, 1)
    },
    addService() {
      this.form.services.push({ name: '' })
    },
    removeService(index) {
      this.form.services.splice(index, 1)
    },
    async saveConfig() {
      // if (!this.form.logo) {
      //   this.$message.error('Logo 为必填项')
      //   return
      // }
      // 先保存 logo
if (this.form.logo) {
  const logoPayload = {
    id: this.logoConfigId,
    name: 'homepage_logo',
    value: this.form.logo
  }
  await this.$http({
    url: `config/${this.logoConfigId ? 'update' : 'save'}`,
    method: 'post',
    data: logoPayload
  })
} else if (this.logoConfigId) {
  // 如果logo为空且之前存在配置，则删除配置
  await this.$http({
    url: `config/delete`,
    method: 'post',
    data: [this.logoConfigId]
  })
  this.logoConfigId = null
}

      this.saving = true
      try {
        // 先保存 logo
        const logoPayload = {
          id: this.logoConfigId,
          name: 'homepage_logo',
          value: this.form.logo
        }
        await this.$http({
          url: `config/${this.logoConfigId ? 'update' : 'save'}`,
          method: 'post',
          data: logoPayload
        })

        // 再保存 footer JSON
        const footerObj = {
          address: this.form.address,
          phone: this.form.phone,
          branches: this.form.branches,
          socials: this.form.socials,
          services: this.form.services,
          extra: this.form.extra,
          backgroundColor: this.form.backgroundColor
        }
        const footerPayload = {
          id: this.footerConfigId,
          name: 'homepage_footer_config',
          value: JSON.stringify(footerObj)
        }
        await this.$http({
          url: `config/${this.footerConfigId ? 'update' : 'save'}`,
          method: 'post',
          data: footerPayload
        })

        this.$message.success(this.$t('common.saveSuccess'))
        this.loadConfig()
      } catch (e) {
        console.error('save homepage config error', e)
        this.$message.error(this.$t('common.saveFailed'))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.homepage-form {
  max-width: 900px;
  padding: 16px 24px;
}

.homepage-form h2 {
  margin-bottom: 12px;
}
</style>


